import { type AtomEffect, atom, atomFamily, selector } from "recoil";
import { FilterName, FilterSelector } from "./filter";
import {
	PageElementsFamily,
	PageIndex,
	TapIdContainers,
	createLocation,
} from "./page";

import {
	isControlChar,
	notDisplayedKeywords,
	strings,
} from "@quickpoint.me/shared";
import {
	ReadingRulerActiveAtomId,
	ReadingRulerMode,
	localStorageEffect,
} from "./accessibility";
import { ParsedCode } from "./selectors";

export const HostId = atom<string>({
	key: "VS_HostId",
	default: "",
});

export const IsEmbedded = atom<boolean>({
	key: "VS_AllowEmbeddedDataFetch",
	default: false,
});

export interface AutoPlayType {
	autoPlay: false | number;
	loop: "once" | "forever";
}

export const AutoPlayAtom = atom<AutoPlayType>({
	key: "VS_AutoPlayAtom",
	default: {
		autoPlay: -1,
		loop: "once",
	},
});

export const AutoSpaceAtom = atom<boolean>({
	key: "VS_AutoSpaceAtom",
	default: true,
});

export const AutoSpaceFilterSetting = selector<boolean>({
	key: "VS_AutoSpaceFilterSetting",
	get: ({ get }) => {
		const filterName = get(FilterName);
		const filter = get(FilterSelector(filterName));
		return filter.autospace;
	},
});

export const ConstrainDimensionsSelector = selector<boolean>({
	key: "VS_ConstrainDimensionsSelector",
	get: ({ get }) => {
		const isEmbedded = get(IsEmbedded);
		const isInAuthor = get(IsInAuthor);
		const isInPresentationMode = get(IsInPresentationMode);
		if (isInPresentationMode) return false;
		return isEmbedded || isInAuthor;
	},
});

export const ContainerSelector = atom<string>({
	key: "VS_ContainerSelector",
});

export const CurrentGridClass = atom<string>({
	key: "VS_CurrentGridClass",
	default: strings.unset,
});

const getDebugModeFromUrl: AtomEffect<boolean> = ({ setSelf }) => {
	const pathname = window.location.pathname.split("/");
	// if the url is a read url it will be shaped quickPoint.me/org/stack/read/pageIdx
	// if not it will be quickPoint.me/org/stack/pageIdx
	// ascertain if read is present or not.
	const debugMode = pathname.indexOf(strings.reservedWords.debug) > -1;
	setSelf(debugMode);
};

export const DebugMode = atom<boolean>({
	key: "VS_DebugMode",
	default: false,
	effects: [getDebugModeFromUrl],
});

export const EditMode = atom<boolean>({
	key: "VS_EditMode",
	default: false,
});

export const IsInAuthor = atom<boolean>({
	key: "VS_IsEmbedded",
	default: false,
});

export const IsOverflowing = atom<boolean>({
	key: "VS_IsOverflowing",
	default: false,
});
export const IsPreviewGenerator = atom<boolean>({
	key: "VS_IsPreviewGenerator",
	default: false,
});

// stack is being viewed in pop-out window from author
export const IsInPopOut = atom<boolean>({
	key: "VS_IsInPopOut",
	default: false,
});

// stack is being viewed in pop-out window from author
export const IsPopOutInDev = atom<boolean>({
	key: "VS_IsInPopOutInDev",
	default: false,
});

export const IsInPresentationMode = atom<boolean>({
	key: "VS_IsInPresentationMode",
	default: false,
});

export const SingleColumnInPortrait = selector<boolean>({
	key: "VS_SingleColumnInPortrait",
	get: ({ get }) => {
		const filterName = get(FilterName);
		const filter = get(FilterSelector(filterName));

		return filter.grid.singleColumnInPortrait;
	},
});

// each time a user taps the idx changes to trigger the UI update
export const TapEvent = selector<TapDirectionType>({
	key: "VS_TapEvent",
	get: ({ get }) => {
		const direction = get(TapDirection);
		return direction;
	},
	set: ({ get, set }, direction) => {
		const inAuthor = get(IsInAuthor);
		const inEditMode = get(EditMode);
		const inPresentationMode = get(IsInPresentationMode);
		const inPopOut = get(IsInPopOut);

		// the embedded preview stays page-locked to the editor; the pop-out is
		// navigable — its page turns are reported back so the tie is two-way
		if (!inPresentationMode && inAuthor && inEditMode && !inPopOut) return;
		// an array of ids of page elements grouped into containers
		// when the user taps a containers elements are made visible and the container is marked as tapped.
		const pageIndex = get(PageIndex);
		const tapIdContainers = get(TapIdContainers(pageIndex));
		const parsedCode = get(ParsedCode);
		const isReadingRuler = get(ReadingRulerMode);

		if (parsedCode.length === 0) {
			return;
		}

		// const readMode = get(ReadMode)
		// const stackCfKey = get(StackCfKey)

		// how many pages are there?
		const maxPageIndex = parsedCode.length; // page Index is base 1
		// catch a pageIndex that's been set by the URL and is broken —
		// clamp and stop, the rest of the logic would run against the bad index
		if (pageIndex > maxPageIndex) {
			set(PageIndex, maxPageIndex);
			return;
		}
		if (pageIndex < 1) {
			set(PageIndex, 1);
			return;
		}

				if (isReadingRuler) {
					const activeAtomId = get(ReadingRulerActiveAtomId);
					const currentPageCode = parsedCode[pageIndex - 1];
		
					// Determine active container (last one tapped)
					let activeContainerIndex = -1;
					for (let i = tapIdContainers.length - 1; i >= 0; i--) {
						if (tapIdContainers[i].containerTapped) {
							activeContainerIndex = i;
							break;
						}
					}
					if (activeContainerIndex === -1) activeContainerIndex = 0; // Fallback
		
					const currentContainer = tapIdContainers[activeContainerIndex];
		
					// Filter for visual atoms only
					const getVisualIds = (ids: number[]) =>
						ids.filter((id) => {
							const type = currentPageCode[id].type;
							return type !== "blank:" && type !== "background:" && !notDisplayedKeywords.includes(type) && !isControlChar(type);
						});
		
					const validIdsInContainer = getVisualIds(currentContainer.ids);
					const currentIndexInList = validIdsInContainer.indexOf(activeAtomId);
		
					if (direction === "forward") {
						// 1. Move to next item in SAME container
						if (currentIndexInList < validIdsInContainer.length - 1) {
							const nextId = validIdsInContainer[currentIndexInList + 1];
							set(ReadingRulerActiveAtomId, nextId);
							return;
						}
		
						// 2. Open NEXT container
						if (activeContainerIndex < tapIdContainers.length - 1) {
							const nextContainer = tapIdContainers[activeContainerIndex + 1];
		
							// Reveal container
							set(TapIdContainers(pageIndex), (prev) => {
								const newVal = prev.map((c) => ({ ...c })); // deep copy needed? map shallow copy is safer
								newVal[activeContainerIndex + 1].containerTapped = true;
								return newVal;
							});
		
							// Reveal items
							for (const id of nextContainer.ids) {
								const key = createLocation(pageIndex, id);
								set(PageElementsFamily(key), (prev) => ({
									...prev,
									data: { ...prev.data, tapped: true },
								}));
							}
		
							// Set Ruler to first item
							const validIdsInNext = getVisualIds(nextContainer.ids);
							if (validIdsInNext.length > 0) {
								set(ReadingRulerActiveAtomId, validIdsInNext[0]);
							}
							return;
						}
		
						// 3. Next Page
						if (pageIndex < maxPageIndex) {
							set(PageIndex, pageIndex + 1);
							set(ReadingRulerActiveAtomId, -1);
						}
						return;
					}
									// Basic Back implementation
							if (direction === "back") {
								// 1. Prev item in SAME container
								if (currentIndexInList > 0) {
									set(
										ReadingRulerActiveAtomId,
										validIdsInContainer[currentIndexInList - 1],
									);
									return;
								}
								// 2. Prev container
								if (activeContainerIndex > 0) {
									// Note: In standard logic we don't usually re-hide containers when going back step-by-step unless we go all the way back?
									// User didn't specify strict reverse, but logical reverse means jumping to end of prev container.
									const prevContainer = tapIdContainers[activeContainerIndex - 1];
									const validIdsInPrev = getVisualIds(prevContainer.ids);
									if (validIdsInPrev.length > 0) {
										set(
											ReadingRulerActiveAtomId,
											validIdsInPrev[validIdsInPrev.length - 1],
										);
									}
									// Optional: Hide current container?
									// For now, let's just move the highlighter.
									return;
								}
				
								// 3. Prev Page
								if (pageIndex > 1) {
									set(PageIndex, pageIndex - 1);
									set(ReadingRulerActiveAtomId, -1);
								}
								return;
							}		}

		// if direction is back, decrement pageIdx.. render page must show all elements
		if (direction === "back" && pageIndex > 1) {
			// moving backwards we want to see all the hidden elements on the
			// DESTINATION page (pageIndex - 1) — RenderPage rebuilds that page
			// reading ShowAllTapElements(destination)
			set(ShowAllTapElements(pageIndex - 1), true);
			set(PageIndex, (prev) => {
				return prev - 1;
			});
		}

		// if direction is forward
		if (direction === "forward") {
			// anything left on the screen untapped?
			const untappedContainer = tapIdContainers.find(
				(container) => container.containerTapped === false,
			);
			if (untappedContainer) {
				// yes? whats next to be tapped?
				// set the container to tapped and all it's children to tapped,
				for (const id of untappedContainer.ids) {
					const key = createLocation(pageIndex, id);
					set(PageElementsFamily(key), (prev) => ({
						...prev,
						data: { ...prev.data, tapped: true },
					}));
				}

				// set the container to tapped
				set(TapIdContainers(pageIndex), (prev) => {
					const unTappedIndex = tapIdContainers.findIndex(
						(container) => container === untappedContainer,
					);
					// deep copy
					const newValue = prev.map((el) => {
						return { ...el };
					});
					newValue[unTappedIndex].containerTapped = true;
					return newValue;
				});
			}

			// no? go to next page and RenderPage should run
			if (!untappedContainer && pageIndex < maxPageIndex) {
				// arriving forwards the destination reveals progressively —
				// clear any show-all left over from an earlier back visit
				set(ShowAllTapElements(pageIndex + 1), false);
				set(PageIndex, (prev) => prev + 1);
			}
		}
	},
});

const getReadModeFromUrl: AtomEffect<boolean> = ({ setSelf }) => {
	const pathname = window.location.pathname.split("/");
	// if the url is a read url it will be shaped quickPoint.me/org/stack/read/pageIdx
	// if not it will be quickPoint.me/org/stack/pageIdx
	// ascertain if read is present or not.
	const readMode = pathname.indexOf(strings.reservedWords.read) > -1;
	setSelf(readMode);
};
/**
 * Sets the viewer to read mode
 */
export const ReadMode = atom<boolean>({
	key: "VS_ReadMode",
	default: false,
	effects: [getReadModeFromUrl],
});

export type TapDirectionType = "forward" | "back" | "init";

export const TapDirection = atom<TapDirectionType>({
	key: "VS_TapDirection",
	default: "init",
});

// when rendering show all elements that would be hidden unless tapped
// used for editing mode and for moving backwards
export const ShowAllTapElements = atomFamily<boolean, number>({
	key: "VS_ShowAllTapElements",
	default: false,
});

export const ShowInfoBarAtom = atom({
	key: "VS_ShowInfoBarAtom",
	default: false,
	effects: [localStorageEffect("show_info_bar")],
});

export const ShowSettingsAtom = atom<boolean>({
	key: "VS_ShowSettings",
	default: false,
});

export const ShowSourceCodeAtom = atom<boolean>({
	key: "VS_ShowSourceCode",
	default: false,
});
